import React, { useEffect, useState } from "react";
import { Button, Dialog, Skeleton } from "@/components/ui";
import { FormProvider, useForm } from "react-hook-form";
import { FormServices, EligibilityCriterion } from "@/services/bedrock";
import { EligibilityEditor } from "@/containers/Coach/EligibilityEditor";
import { CoachService, GenericAccess } from "@/services/CoachServices";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: number | undefined;
}

interface FormEligibilityValues {
  id: number | null;
  eligibilityCriterions: EligibilityCriterion[];
}

const FormEligibilityDialog: React.FC<Props> = ({ open, formId, onOpenChange }) => {
  const queryClient = useQueryClient();
  const { coachId } = useSocialCoachSession();

  const methods = useForm<FormEligibilityValues>({
    defaultValues: {
      id: null,
      eligibilityCriterions: [],
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, isDirty },
  } = methods;

  const [originalAccess, setOriginalAccess] = useState<GenericAccess>({ eligibilityCriteria: [] });
  const [currentAccess, setCurrentAccess] = useState<GenericAccess>({ eligibilityCriteria: [] });
  const [initializing, setInitializing] = useState(true);

  const getAccess = async (criteria: EligibilityCriterion[]) => {
    return await CoachService.getAccessPreview(String(coachId ?? ""), "VIDEO_PAGES", criteria);
  };

  useEffect(() => {
    if (!open || !formId) return;

    setInitializing(true);
    FormServices.getForm(formId, { extraFields: ["eligibility_criterions"] })
      .then(form => {
        const criteria = form.eligibilityCriterions || [];
        reset({
          id: formId,
          eligibilityCriterions: criteria,
        });
        return getAccess(criteria);
      })
      .then(response => {
        setCurrentAccess(response);
        setOriginalAccess(response);
      })
      .catch(error => {
        console.error(error);
        toast.error("Error getting form eligibility");
        onOpenChange(false);
      })
      .finally(() => {
        setInitializing(false);
      });
  }, [open, formId]);

  const updateHandler = React.useCallback(
    async (newCriteria: EligibilityCriterion[]) => {
      const access = await getAccess(newCriteria);
      setCurrentAccess(access);
      setValue("eligibilityCriterions", newCriteria, { shouldDirty: true });
      return access;
    },
    [coachId, setValue]
  );

  const handleSaveSubmit = (data: FormEligibilityValues) => {
    if (!formId) return;

    return FormServices.updateForm(formId, { eligibilityCriterions: data.eligibilityCriterions })
      .then(() => {
        toast.success("Form eligibility updated successfully");
        queryClient.invalidateQueries({ queryKey: ["forms"] });
      })
      .then(() => onOpenChange(false))
      .catch(error => {
        console.error(error);
        toast.error("Error updating form eligibility");
      });
  };

  return (
    <Dialog open={open} size="xl" className="top-16 translate-y-0" onOpenChange={onOpenChange}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSaveSubmit)} className="flex flex-col">
          <Dialog.Header>
            <Dialog.Title>Edit Form Eligibility</Dialog.Title>
          </Dialog.Header>
          <Dialog.Content className="max-h-[60vh] overflow-y-auto">
            <div className="w-full p-2">
              {initializing ? (
                <Skeleton className="w-full h-80" />
              ) : (
                <EligibilityEditor
                  originalAccess={originalAccess}
                  currentAccess={currentAccess}
                  accessType="FORM"
                  updateHandler={updateHandler}
                  isNew={false}
                />
              )}
            </div>
          </Dialog.Content>
          <Dialog.Footer>
            {formId && (
              <div className="flex justify-end items-center gap-2">
                <Button type="button" variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!isDirty} loading={isSubmitting}>
                  Save
                </Button>
              </div>
            )}
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export { FormEligibilityDialog };
